import { Link } from "@tanstack/react-router";
import { NMW, TAX_YEAR, gbp } from "@/lib/statutory";

export function NMWDeepContent() {
  const rates = [
    { band: "National Living Wage (21 and over)", rate: NMW.nationalLivingWage21Plus, note: "Applies from the first pay reference period on or after the worker's 21st birthday." },
    { band: "18 to 20 year old rate", rate: NMW.age18to20, note: "Workers in this band move to the National Living Wage at 21." },
    { band: "16 to 17 year old rate", rate: NMW.under18, note: "Above compulsory school age but under 18." },
    { band: "Apprentice rate", rate: NMW.apprentice, note: "Apprentices aged under 19, or aged 19+ in the first year of their apprenticeship. They move to the age-appropriate rate after the first year." },
  ];

  return (
    <section className="px-6 py-12 lg:px-8 lg:py-16">
      <div className="mx-auto max-w-3xl space-y-12 text-foreground">
        <header className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-widest text-accent">In-depth guidance</p>
          <h2 className="font-serif text-3xl text-brand sm:text-4xl">National Minimum Wage and National Living Wage explained</h2>
          <p className="text-base leading-relaxed text-muted-foreground">
            The {TAX_YEAR} National Minimum Wage (NMW) and National Living Wage (NLW) are statutory hourly floors set by
            government under the National Minimum Wage Act 1998. They apply to almost every UK worker, not just employees,
            and HMRC enforces them with penalties of up to 200% of arrears plus public naming. The calculator above checks a
            single pay rate against the right age band, but most underpayment cases come from the rules below, deductions,
            unpaid time, salaried hours and accommodation, rather than the headline rate.
          </p>
        </header>

        <div>
          <h3 className="font-serif text-2xl text-brand">Current UK minimum wage rates ({TAX_YEAR})</h3>
          <p className="mt-3 text-sm text-muted-foreground">
            Rates change each April. Always pay the new rate from the start of the first pay reference period that begins on
            or after 1 April.
          </p>
          <div className="mt-5 overflow-hidden rounded-2xl border border-border">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Band</th>
                  <th className="px-4 py-3">Hourly rate</th>
                  <th className="px-4 py-3">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-card">
                {rates.map((r) => (
                  <tr key={r.band}>
                    <td className="px-4 py-3 font-medium text-foreground">{r.band}</td>
                    <td className="px-4 py-3 font-mono text-foreground">{gbp(r.rate, { decimals: 2 })}</td>
                    <td className="px-4 py-3 text-muted-foreground">{r.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <h3 className="font-serif text-2xl text-brand">Who is entitled to the National Minimum Wage?</h3>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">
            NMW covers workers, not just employees. That includes part-time and casual staff, agency workers, most
            apprentices, paid trainees, piece workers and home workers. It does not cover the genuinely self-employed,
            volunteers, company directors with no contract, students on a required work placement of up to a year, or
            members of the armed forces. If you classify someone as self-employed but control their hours, equipment and
            output, HMRC will treat them as a worker for NMW purposes regardless of the contract label.
          </p>
        </div>

        <div>
          <h3 className="font-serif text-2xl text-brand">What counts as working time for NMW</h3>
          <ul className="mt-3 space-y-2 text-base leading-relaxed text-muted-foreground">
            <li>• Time spent working, including overtime, paid or unpaid.</li>
            <li>• Training time that the employer requires, on or off site.</li>
            <li>• Travel between assignments during the working day (not the commute from home).</li>
            <li>• Time on standby or "on call" at the workplace, even when no work is happening.</li>
            <li>• Time spent waiting to start work, collect materials, or undergo security checks at the end of a shift.</li>
            <li>• Sleep-in shifts where the worker is required to be available, except where statute or recent case law treats availability for sleep as unmeasured time, the HMRC position changes; check before relying on it.</li>
          </ul>
        </div>

        <div>
          <h3 className="font-serif text-2xl text-brand">Common causes of NMW underpayment</h3>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">
            Most employers HMRC names did not deliberately pay below the rate. They tripped over one of these:
          </p>
          <ul className="mt-3 space-y-2 text-base leading-relaxed text-muted-foreground">
            <li>• <strong className="text-foreground">Unpaid working time</strong>, security checks, mandatory pre-shift handovers, locking up.</li>
            <li>• <strong className="text-foreground">Deductions for uniform, tools or training</strong>, anything bought from the employer or required for the role reduces NMW pay even if the wage slip looks compliant.</li>
            <li>• <strong className="text-foreground">Salary sacrifice schemes</strong>, pension or cycle-to-work that drops gross pay below the hourly floor.</li>
            <li>• <strong className="text-foreground">Apprentice rate misuse</strong>, keeping someone on the apprentice rate past their first year or after they turn 19.</li>
            <li>• <strong className="text-foreground">Birthday rate changes missed</strong>, failing to uplift on the first pay reference period after an 18th or 21st birthday.</li>
            <li>• <strong className="text-foreground">Tips and service charge</strong>, these never count towards NMW, even if paid through payroll.</li>
            <li>• <strong className="text-foreground">Accommodation offset</strong>, charging more than the daily offset (£10.66 in {TAX_YEAR}) creates a notional underpayment.</li>
          </ul>
        </div>

        <div>
          <h3 className="font-serif text-2xl text-brand">Salaried hours workers</h3>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">
            For salaried staff, divide annual salary by the contracted basic annual hours, then compare against the
            appropriate NMW rate. If the worker performs more hours than the annual contract allows in a pay reference
            period, you must top up to NMW for the excess hours, even though they sit inside the annual salary.
            Bonus and commission count towards NMW in the pay reference period when they are paid, not when they are earned.
          </p>
        </div>

        <div>
          <h3 className="font-serif text-2xl text-brand">Enforcement, arrears and penalties</h3>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">
            HMRC's National Minimum Wage team can investigate up to six years of pay records on a worker's complaint or
            on a risk-based audit. If they find underpayment they will issue a Notice of Underpayment requiring you to
            repay arrears at the current rate (not the rate at the time), plus a penalty of 200% of the arrears, capped at
            £20,000 per worker. Employers underpaying by £500 or more across the investigation period are routinely named
            by the Department for Business and Trade. Workers can also bring an unlawful deduction from wages claim in the
            employment tribunal in parallel.
          </p>
        </div>

        <div>
          <h3 className="font-serif text-2xl text-brand">If you discover an underpayment</h3>
          <ol className="mt-3 list-decimal space-y-2 pl-6 text-base leading-relaxed text-muted-foreground">
            <li>Stop the underpayment in the next pay reference period.</li>
            <li>Calculate arrears at <em>current</em> NMW rates, not historic.</li>
            <li>Pay arrears as additional wages, never as a one-off bonus, which attracts different tax.</li>
            <li>Document the cause and the correction in writing for each affected worker.</li>
            <li>Self-correct before HMRC contacts you, penalties still apply on investigation but voluntary disclosure reduces risk of naming.</li>
          </ol>
        </div>

        <div className="rounded-2xl border border-accent/30 bg-accent/5 p-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-accent">Related tools and guidance</p>
          <ul className="mt-3 space-y-2 text-base leading-relaxed text-foreground">
            <li>
              <Link to="/hr-calculators/$slug" params={{ slug: "holiday-entitlement-calculator" }} className="font-medium text-brand underline decoration-accent underline-offset-4 hover:text-accent">
                Holiday entitlement calculator
              </Link>: rolled-up holiday pay must still average at NMW.
            </li>
            <li>
              <Link to="/hr-calculators/$slug" params={{ slug: "ssp-calculator" }} className="font-medium text-brand underline decoration-accent underline-offset-4 hover:text-accent">
                Statutory Sick Pay calculator
              </Link>: SSP is paid in addition to wages and does not affect NMW compliance for working time.
            </li>
            <li>
              <Link to="/services/$slug" params={{ slug: "hr-health-check" }} className="font-medium text-brand underline decoration-accent underline-offset-4 hover:text-accent">
                HR health check
              </Link>: full review of payroll, deductions and worker classification against NMW rules.
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
}
